import { useParams } from "react-router-dom";
import { TitlePage } from "../../components/ui/TitlePage";
import { useEffect, useState, useCallback } from "react";
import { api, urls } from "../../services/api";
import { ItemCategory } from "../../components/ui/ItemCategory";
import FlatList from "flatlist-react/lib";
import { IArticle } from "../../types/article";
import { IMenuItem, IMenu } from "../../types/menu";
import { useApp } from "../../context/App";
import { ServicesAccordion } from "../../components/sections/ServicesAccordion";
import { Banner } from "../../components/sections/Banner";
import { NewsShare } from "../../components/sections/NewsShare";
import { Helmet } from "react-helmet-async";
import DOMPurify from 'dompurify';
import { motion } from "framer-motion";

export function ArticleScreen() {

	let { id } = useParams();

	const { mobile, configApiURL, getTenantUrl } = useApp()

	const [article, setArticle] = useState<IArticle>(
		{
			content: '',
			published: '',
			title: 'Carregando...'
		}
	)

	const [listMenu, setMenuList] = useState<IMenu[]>([])
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [id])

	const getArticleById = useCallback(async (articleId: number) => {
		try {
			const response = await api.get(urls.Article + articleId)
			setArticle(response.data)
		} catch (error) {
			// Error silenced for production cleanliness
		} finally {
			setIsLoading(false)
		}
	}, []);

	useEffect(() => {
		if (id) {
			setIsLoading(true)
			getArticleById(parseInt(id));
		}
	}, [id, getArticleById]);

	useEffect(() => {
		(async () => {
			try {
				const response = await api.get(urls.menus)
				const menu: IMenuItem[] = response.data
				let menuFiltered: IMenu[] = []
				let idSection = 0

				if (menu && menu.length > 0 && menu[0].secoes) {
					menu[0].secoes.forEach(itemSecoes => {
						itemSecoes.menus?.forEach(itemMenu => {
							if (parseInt(id || '0') === itemMenu.ref_cod_artigo) {
								idSection = itemMenu.ref_cod_menu_secao || 0
							}
						})
						if (itemSecoes.cod_menu_secao === idSection) {
							menuFiltered = itemSecoes.menus as IMenu[]
						}
					})
				}

				setMenuList([...menuFiltered])
			} catch (error) {
				// silent
			}
		})()
	}, [id])



	const url = window.location.href
	const url_split = url.split("/")
	const artigo_id_url = Number(url_split[url_split.length - 1])

	const apiUrl = configApiURL.replace("portaladm-pmitajai/api/", "public/")

	// Transform sequence:
	// 1. Fix images src="/... to point to full API public path
	// 2. Fix links pointing to itajai.sc.gov.br/e/XXX or /a/XXX to use new friendly URLs
	let content = article?.content.replaceAll('src="/', `src="${apiUrl}`)
	content = content.replaceAll('https://itajai.sc.gov.br/e/', '/e/')
	content = content.replaceAll('http://itajai.sc.gov.br/e/', '/e/')
	content = content.replaceAll('https://itajai.sc.gov.br/a/', '/a/')
	content = content.replaceAll('http://itajai.sc.gov.br/a/', '/a/')
	content = content.replaceAll('https://itajai.sc.gov.br/', '/')
	content = content.replaceAll('http://itajai.sc.gov.br/', '/')

	if (mobile) {
		return (
			<div className="flex flex-col w-full min-h-screen bg-white dark:bg-surface-dark transition-colors items-center justify-start [&_img]:!w-full [&_img]:!h-auto [&_img]:mb-12">
				<Helmet>
					<title>{article.title} | Município de Itajaí</title>
					<meta property="og:title" content={article.title} />
					<meta property="og:type" content="article" />
					<meta property="og:url" content={window.location.href} />
				</Helmet>

				<TitlePage title={article?.title ? article?.title : (isLoading ? 'Carregando...' : 'Sem Nome')} />

				<motion.div
					key={id}
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					className="flex flex-row w-11/12 mt-6 lg:mt-6 mb-12"
				>
					<div
						className="w-11/12 text-gray-800 dark:text-gray-200 text-base leading-relaxed break-words font-light dark-prose"
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
					/>
				</motion.div>

				<div className="flex flex-row w-11/12 mt-3">
					<NewsShare />
				</div>

				<ServicesAccordion />
				<Banner mobile={mobile} />
				<div className="h-33" />
			</div>
		)
	}

	return (
		<div className="flex flex-col w-full min-h-screen bg-white dark:bg-surface-dark transition-colors items-center justify-start">
			<Helmet>
				<title>{article.title} | Município de Itajaí</title>
				<meta property="og:title" content={article.title} />
				<meta property="og:type" content="article" />
				<meta property="og:url" content={window.location.href} />
			</Helmet>

			<TitlePage
				title={article?.title ? article?.title : (isLoading ? 'Carregando...' : 'Sem Nome')}
			/>

			<motion.div
				key={id}
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4 }}
				className="flex flex-row w-3/5 mt-3 pb-60 lg:pb-20"
			>
				{listMenu.length > 0 && (
					<div className="flex flex-col w-[30%]">
						<div className="font-roboto font-semibold text-fonte text-sm mb-2 uppercase"></div>
						<FlatList
							list={listMenu}
							renderItem={(item) => (
								<ItemCategory
									key={item.cod_menu}
									title={item.titulo || ''}
									isActive={item.ref_cod_artigo === artigo_id_url}
									href={getTenantUrl(item.ref_cod_artigo ? `/a/${item.ref_cod_artigo}` : (item.ref_cod_entidade ? `/e/${item.ref_cod_entidade}` : (item.link && item.link !== '#' ? item.link : '#')))}
									className="text-sm mb-1 uppercase"
								/>
							)}
						/>
					</div>
				)}

				{article.content ? (
					<div
						className={listMenu.length > 0 ? "w-3/5 text-gray-800 dark:text-gray-200 text-base leading-relaxed break-words font-light dark-prose" : "w-full text-gray-800 dark:text-gray-200 text-base leading-relaxed break-words font-light dark-prose"}
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
					/>
				) : (
					<div
						className={listMenu.length > 0 ? "flex flex-col w-3/5 items-center text-gray-500 dark:text-gray-400" : "w-full text-gray-500 dark:text-gray-400"}
					>Sem texto</div>
				)}
			</motion.div>

			<div className="flex flex-row w-3/5 mt-3 pb-60 lg:pb-20">
				<NewsShare />
			</div>
		</div>
	);
}